import { CSSProperties, ReactNode, useEffect, useMemo, useState } from "react";
import { getTg } from "./telegram.js";
import { api } from "./api.js";
import { RemindersTab } from "./tabs/Reminders.js";
import { ClientsTab, ClientDetail } from "./tabs/Clients.js";
import { ReportsTab } from "./tabs/Reports.js";
import { MastersTab } from "./tabs/Masters.js";
import {
  IconBell,
  IconChart,
  IconSparkle,
  IconSparkleSolid,
  IconUsers,
} from "./components/NavIcons.js";
import { ThemeModernContext } from "./theme.js";

type Tab = "reminders" | "clients" | "masters" | "analytics";

/** Deep-link from a digest notification: ?startapp=c<clientId> opens a profile. */
function deepLinkClientId(): string | null {
  const param = getTg()?.initDataUnsafe?.start_param ?? "";
  const m = /^c(.+)$/.exec(param);
  return m ? m[1]! : null;
}

export function App() {
  const [tab, setTab] = useState<Tab>("reminders");
  const [me, setMe] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deepClientId, setDeepClientId] = useState<string | null>(
    deepLinkClientId,
  );
  // Hide the floating bottom nav while an input/textarea is focused.
  // Otherwise Telegram shrinks the viewport for the on-screen keyboard
  // and the nav (position:fixed; bottom:20) lands directly on top of the
  // textarea — owner couldn't reach the comment field.
  const [kbOpen, setKbOpen] = useState(false);
  // Opt-in modern theme — flipped via the ✨ pill (dev-only). Persisted
  // to localStorage so it survives reloads. Default = legacy look.
  const [themeModern, setThemeModern] = useState<boolean>(() => {
    try {
      return localStorage.getItem("nz_theme") === "modern";
    } catch {
      return false;
    }
  });
  // FORCE-MODERN список (пренк від Ігоря): для цих TG ID модерн-тема
  // вмикається примусово, незалежно від localStorage. Toggle їм не показується.
  // 334757434 — Люба (owner), 313855642 — Льоша.
  const FORCE_MODERN_IDS = useMemo(
    () => new Set(["334757434", "313855642"]),
    [],
  );
  const forcedModern = !!me && FORCE_MODERN_IDS.has(String(me.telegramId));
  const effectiveModern = forcedModern || themeModern;
  useEffect(() => {
    document.body.classList.toggle("theme-modern", effectiveModern);
    if (forcedModern) return; // не пишемо у localStorage у форс-режимі
    try {
      localStorage.setItem("nz_theme", themeModern ? "modern" : "legacy");
    } catch {
      /* ignore */
    }
  }, [effectiveModern, forcedModern, themeModern]);

  useEffect(() => {
    const tg = getTg();
    tg?.ready();
    tg?.expand();
    api
      .me()
      .then(setMe)
      .catch((e) => setError(String(e)));
  }, []);

  useEffect(() => {
    const isInput = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      if (!el) return false;
      const tag = el.tagName;
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        (el as HTMLElement).isContentEditable
      );
    };
    const onFocus = (e: FocusEvent) => {
      if (isInput(e.target)) setKbOpen(true);
    };
    const onBlur = (e: FocusEvent) => {
      if (isInput(e.target)) setKbOpen(false);
    };
    document.addEventListener("focusin", onFocus);
    document.addEventListener("focusout", onBlur);
    return () => {
      document.removeEventListener("focusin", onFocus);
      document.removeEventListener("focusout", onBlur);
    };
  }, []);

  if (error) {
    return (
      <div className="card">
        <h1 style={{ margin: 0 }}>Nailzona</h1>
        <p style={{ marginTop: 12 }}>Доступ заборонено.</p>
        <p className="hint" style={{ marginTop: 4 }}>{error}</p>
      </div>
    );
  }
  if (!me) {
    return <div className="hint">Завантаження...</div>;
  }

  if (me.role === "master") {
    return (
      <ThemeModernContext.Provider value={effectiveModern}>
        <MasterStub master={me.master} modern={effectiveModern} />
      </ThemeModernContext.Provider>
    );
  }

  // Opened straight to a client profile from a digest notification link.
  if (deepClientId) {
    return (
      <>
        <h1>Nailzona</h1>
        <ClientDetail
          id={deepClientId}
          onBack={() => setDeepClientId(null)}
        />
      </>
    );
  }

  const greetingName =
    me.name?.trim()?.split(/\s+/)[0] ??
    (me.isOwner ? "Любо" : me.isDeveloper ? "Ігоре" : "усе чьотко");

  return (
    <ThemeModernContext.Provider value={effectiveModern}>
    <div style={{ paddingBottom: 110 }}>
      <div
        className="nz-topbar"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        {effectiveModern ? (
          <div className="nz-brand">
            <div className="nz-logo">
              <IconSparkleSolid style={{ color: "#fff" }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="nz-wordmark">Nailzona</div>
              <div className="nz-greet">Доброго дня, {greetingName} 🌸</div>
            </div>
          </div>
        ) : (
          <h1 style={{ margin: 0, flex: 1 }}>Nailzona</h1>
        )}
        {me.isDeveloper && (
          <button
            onClick={() => setThemeModern((v) => !v)}
            className="nz-theme-pill"
            title={
              themeModern
                ? "Вимкнути новий дизайн"
                : "Увімкнути новий дизайн (beta)"
            }
            style={
              themeModern
                ? {
                    border: 0,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                  }
                : {
                    padding: "6px 14px",
                    borderRadius: 999,
                    border: 0,
                    background: "rgba(127,127,127,0.15)",
                    color: "var(--text)",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    lineHeight: 1,
                  }
            }
          >
            {themeModern ? (
              <>
                <IconSparkleSolid style={{ color: "var(--rose-500)" }} /> новий
              </>
            ) : (
              <>✨ старий</>
            )}
          </button>
        )}
      </div>

      {tab === "reminders" && <RemindersTab />}
      {tab === "clients" && <ClientsTab />}
      {tab === "masters" && <MastersTab />}
      {tab === "analytics" && me.isOwner && <ReportsTab />}

      {/* Floating bottom nav — pill 20px above the screen edge. Hidden
          while a text field is focused so the keyboard doesn't shove the
          nav on top of the input. */}
      {!kbOpen && (
        <nav className="nz-nav nz-bottomnav" style={floatingNav}>
          <NavBtn
            icon={effectiveModern ? <IconBell /> : "🔔"}
            label="Нагадування"
            active={tab === "reminders"}
            onClick={() => setTab("reminders")}
          />
          <NavBtn
            icon={effectiveModern ? <IconUsers /> : "👥"}
            label="Клієнти"
            active={tab === "clients"}
            onClick={() => setTab("clients")}
          />
          <NavBtn
            icon={effectiveModern ? <IconSparkle /> : "👩‍🎨"}
            label="Майстри"
            active={tab === "masters"}
            onClick={() => setTab("masters")}
          />
          {me.isOwner && (
            <NavBtn
              icon={effectiveModern ? <IconChart /> : "📊"}
              label="Аналітика"
              active={tab === "analytics"}
              onClick={() => setTab("analytics")}
            />
          )}
        </nav>
      )}
    </div>
    </ThemeModernContext.Provider>
  );
}

function NavBtn({
  icon,
  label,
  active,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button onClick={onClick} data-active={active} style={navBtn(active)}>
      <span
        style={{
          fontSize: 20,
          lineHeight: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </span>
      <span style={{ fontSize: 10, lineHeight: 1.2 }}>{label}</span>
    </button>
  );
}

const floatingNav: CSSProperties = {
  position: "fixed",
  bottom: 20,
  left: 12,
  right: 12,
  background: "var(--bg)",
  borderRadius: 18,
  padding: 6,
  display: "flex",
  gap: 4,
  justifyContent: "stretch",
  boxShadow:
    "0 8px 28px rgba(0,0,0,0.18), 0 0 0 1px rgba(127,127,127,0.18)",
  zIndex: 100,
};

function navBtn(active: boolean): CSSProperties {
  return {
    flex: 1,
    padding: "8px 4px",
    background: active ? "var(--button)" : "transparent",
    color: active ? "var(--button-text)" : "var(--text)",
    border: 0,
    borderRadius: 14,
    fontSize: 10,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    minWidth: 0,
    minHeight: 52,
    fontFamily: "inherit",
  };
}

function MasterStub({
  master,
  modern,
}: {
  master: { id: string; name: string; telegramUsername: string | null };
  modern?: boolean;
}) {
  if (modern) {
    return (
      <div className="nz-welcome">
        <div className="em">💅🌸</div>
        <div className="h">Привіт, {master.name}!</div>
        <div className="t">
          Ти неймовірна 💖
          <br />
          Дякуємо за твою роботу!
        </div>
        <div
          style={{
            marginTop: 24,
            fontSize: 13,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "var(--rose-700)",
            textAlign: "left",
          }}
        >
          📝 Як надсилати касовий звіт
        </div>
        <p
          className="hint"
          style={{ marginTop: 6, marginBottom: 0, textAlign: "left" }}
        >
          Просто напиши боту, починаючи з дати, по рядку на кожну послугу:
        </p>
        <div className="nz-code">
          <div>16.05</div>
          <div>Манікюр <span className="c">1000</span></div>
          <div>Педикюр <span className="c">1200</span>, 100 чай</div>
          <div>Гель-лак <span className="c">900</span> (-20% промокод)</div>
          <div>Ремонт (гарантія)</div>
          <div>Манікюр <span className="c">1000</span> (блогер)</div>
          <div>Чайові <span className="c">200</span></div>
        </div>
        <div
          className="hint"
          style={{
            fontSize: 12.5,
            marginTop: 14,
            lineHeight: 1.6,
            textAlign: "left",
          }}
        >
          • <b>(-20%)</b> або <b>(знижка 20%)</b> — знижка
          <br />• <b>, 100 чай</b> чи <b>(100 чай)</b> — чайові
          <br />• <b>Чайові 200</b> окремим рядком — теж чайові
          <br />• <b>(гарантія)</b> — переробка, 0 грн
          <br />• <b>(блогер)</b> — робота з блогером по бартеру
        </div>
        <p
          className="hint"
          style={{ fontSize: 12, marginTop: 14, textAlign: "left" }}
        >
          Можна додавати фото — вони збережуться у твоєму портфоліо.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        textAlign: "center",
        padding: "32px 16px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 16,
      }}
    >
      <div style={{ fontSize: 64 }}>💅🌸</div>
      <h1 style={{ margin: 0, fontSize: 22 }}>
        Привіт, {master.name}!
      </h1>
      <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5 }}>
        Ти неймовірна 💖
        <br />
        Дякуємо за твою роботу!
      </p>
      <div
        style={{
          background: "var(--secondary-bg)",
          borderRadius: 12,
          padding: 14,
          marginTop: 4,
          fontSize: 14,
          textAlign: "left",
          maxWidth: 360,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          📝 Як надсилати касовий звіт
        </div>
        <p className="hint" style={{ marginBottom: 6 }}>
          Просто напиши боту, починаючи з дати, по рядку на кожну послугу:
        </p>
        <pre
          style={{
            background: "var(--bg)",
            padding: 10,
            borderRadius: 8,
            fontSize: 13,
            margin: 0,
            whiteSpace: "pre-wrap",
          }}
        >{`16.05
Манікюр 1000
Педикюр 1200, 100 чай
Гель-лак 900 (-20% промокод)
Ремонт (гарантія)
Манікюр 1000 (блогер)
Чайові 200`}</pre>
        <div
          className="hint"
          style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }}
        >
          • <b>(-20%)</b> або <b>(знижка 20%)</b> — знижка
          <br />• <b>, 100 чай</b> чи <b>(100 чай)</b> — чайові
          <br />• <b>Чайові 200</b> окремим рядком — теж чайові
          <br />• <b>(гарантія)</b> — переробка, 0 грн
          <br />• <b>(блогер)</b> — робота з блогером по бартеру
        </div>
      </div>
      <p className="hint" style={{ fontSize: 12, marginTop: 4 }}>
        Можна додавати фото — вони збережуться у твоєму портфоліо.
      </p>
    </div>
  );
}
